import React from 'react';
import Header from '../../components/Header';
import { Smartphone, Users, Clock, AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo } from '../../src/seo/Seo';

const ServiceUsage: React.FC = () => {
  // Fonctions par défaut pour les props du Header
  const handleDownloadClick = () => {
    // Rediriger vers la page d'accueil pour le téléchargement
    window.location.href = '/';
  };

  const handleMySpaceClick = () => {
    // Rediriger vers la page d'accueil pour My Space
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Seo
        title={{ fr: 'CGU — Utilisation du service — Burok', en: 'Terms — Service usage — Burok' }}
        description={{
          fr: "Règles et conditions d'utilisation du service Burok, bonnes pratiques et activités interdites.",
          en: 'Rules and conditions for using Burok, best practices, and prohibited activities.',
        }}
        canonicalPath={{ fr: '/cgu/service-usage', en: '/en/cgu/service-usage' }}
      />
      <Header onDownloadClick={handleDownloadClick} onMySpaceClick={handleMySpaceClick} />
      
      {/* Hero Section */}
      <section className="px-4 pt-20 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex justify-center items-center mb-6 w-16 h-16 bg-blue-100 rounded-full">
            <Smartphone className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
            Utilisation du Service Burok
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-xl text-gray-600">
            Guide complet des règles d'utilisation et des bonnes pratiques
          </p>
          <Link
            to="/cgu"
            className="inline-flex items-center px-6 py-3 font-medium text-white bg-gray-600 rounded-full transition-all duration-200 hover:bg-gray-700"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Retour aux CGU
          </Link>
        </div>
      </section>

      {/* Content Section */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="p-8 bg-white rounded-2xl shadow-xl md:p-12">
            
            {/* Service Description */}
            <div className="mb-12">
              <h2 className="flex items-center mb-6 text-3xl font-bold text-gray-900">
                <Smartphone className="mr-3 w-6 h-6 text-blue-500" />
                Description Détaillée du Service
              </h2>
              <p className="mb-6 leading-relaxed text-gray-700">
                Burok est une plateforme de gestion financière personnelle sophistiquée qui révolutionne la façon dont vous gérez vos finances. 
                Notre application combine technologie de pointe et simplicité d'utilisation pour offrir une expérience unique dans le domaine 
                de la finance personnelle.
              </p>
              
              <div className="grid gap-8 mb-8 md:grid-cols-2">
                <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <h3 className="mb-4 text-xl font-semibold text-blue-900">Fonctionnalités Principales</h3>
                  <ul className="space-y-3 text-blue-800">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Suivi des dépenses en temps réel avec catégorisation automatique</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Création et gestion de budgets personnalisés par catégorie</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Analyses et rapports financiers détaillés avec graphiques interactifs</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Synchronisation multi-appareils avec chiffrement de bout en bout</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Alertes et notifications personnalisées pour les objectifs financiers</span>
                    </li>
                  </ul>
                </div>
                
                <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                  <h3 className="mb-4 text-xl font-semibold text-purple-900">Spécifications Techniques</h3>
                  <ul className="space-y-3 text-purple-800">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>iOS 13.0+ et Android 8.0+ (API level 26+)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Connexion Internet requise pour la synchronisation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Stockage local sécurisé avec chiffrement AES-256</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Support des notifications push pour alertes importantes</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Usage Rules */}
            <div className="mb-12">
              <h2 className="flex items-center mb-6 text-3xl font-bold text-gray-900">
                <Users className="mr-3 w-6 h-6 text-indigo-500" />
                Règles d'Utilisation Détaillées
              </h2>
              
              <div className="space-y-6">
                <div className="p-6 bg-yellow-50 rounded-xl border border-yellow-200">
                  <h3 className="flex items-center mb-4 text-xl font-semibold text-yellow-900">
                    <AlertTriangle className="mr-3 w-5 h-5 text-yellow-600" />
                    Utilisation Personnelle Uniquement
                  </h3>
                  <p className="mb-4 text-yellow-800">
                    L'application Burok est conçue exclusivement pour la gestion de vos finances personnelles. 
                    Toute utilisation commerciale, professionnelle ou à des fins lucratives est strictement interdite 
                    sans autorisation écrite préalable de Burok.
                  </p>
                  <ul className="space-y-2 text-sm text-yellow-800">
                    <li>• Interdiction d'utiliser l'application pour gérer les finances d'entreprises</li>
                    <li>• Prohibition de la revente ou redistribution des données générées</li>
                    <li>• Utilisation interdite pour des services de conseil financier</li>
                    <li>• Prohibition de l'intégration dans d'autres applications commerciales</li>
                  </ul>
                </div>

                <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="flex items-center mb-4 text-xl font-semibold text-green-900">
                    <CheckCircle className="mr-3 w-5 h-5 text-green-600" />
                    Bonnes Pratiques Recommandées
                  </h3>
                  <ul className="space-y-3 text-green-800">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Mise à jour régulière de l'application pour bénéficier des dernières fonctionnalités de sécurité</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Utilisation de mots de passe forts et uniques pour votre compte</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Activation de l'authentification à deux facteurs pour une sécurité renforcée</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Sauvegarde régulière de vos données importantes</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Vérification périodique de l'activité de votre compte</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Prohibited Activities */}
            <div className="mb-12">
              <h2 className="flex items-center mb-6 text-3xl font-bold text-gray-900">
                <AlertTriangle className="mr-3 w-6 h-6 text-red-500" />
                Activités Strictement Interdites
              </h2>
              
              <div className="p-6 bg-red-50 rounded-xl border border-red-200">
                <p className="mb-6 font-medium text-red-800">
                  Les activités suivantes sont strictement interdites et peuvent entraîner la suspension immédiate 
                  de votre compte, voire des poursuites judiciaires :
                </p>
                
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <h4 className="mb-3 font-semibold text-red-900">Violations de Sécurité</h4>
                    <ul className="space-y-2 text-sm text-red-800">
                      <li>• Tentative de piratage ou d'intrusion dans nos systèmes</li>
                      <li>• Utilisation de bots ou scripts automatisés</li>
                      <li>• Exploitation de vulnérabilités de sécurité</li>
                      <li>• Tentative de contournement des mesures de protection</li>
                      <li>• Distribution de logiciels malveillants</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="mb-3 font-semibold text-red-900">Utilisation Frauduleuse</h4>
                    <ul className="space-y-2 text-sm text-red-800">
                      <li>• Création de comptes multiples frauduleux</li>
                      <li>• Utilisation d'informations d'identification volées</li>
                      <li>• Tentative de fraude ou d'escroquerie</li>
                      <li>• Usurpation d'identité d'autres utilisateurs</li>
                      <li>• Manipulation des données financières</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Availability */}
            <div className="mb-12">
              <h2 className="flex items-center mb-6 text-3xl font-bold text-gray-900">
                <Clock className="mr-3 w-6 h-6 text-indigo-500" />
                Disponibilité et Maintenance du Service
              </h2>
              
              <div className="space-y-6">
                <div className="p-6 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="mb-4 text-xl font-semibold text-blue-900">Engagement de Disponibilité</h3>
                  <p className="mb-4 text-blue-800">
                    Burok s'engage à maintenir une disponibilité de service de 99.5% sur une base mensuelle, 
                    hors périodes de maintenance planifiée et cas de force majeure.
                  </p>
                  <ul className="space-y-2 text-blue-800">
                    <li>• Service disponible 24h/24 et 7j/7</li>
                    <li>• Maintenance planifiée annoncée 48h à l'avance</li>
                    <li>• Support technique disponible en continu</li>
                    <li>• Sauvegarde automatique des données toutes les heures</li>
                  </ul>
                </div>
                
                <div className="p-6 bg-orange-50 rounded-xl border border-orange-200">
                  <h3 className="mb-4 text-xl font-semibold text-orange-900">Maintenance et Mises à Jour</h3>
                  <p className="mb-4 text-orange-800">
                    Pour garantir la sécurité et les performances optimales, Burok effectue des maintenances 
                    régulières et des mises à jour de l'application.
                  </p>
                  <ul className="space-y-2 text-orange-800">
                    <li>• Mises à jour de sécurité appliquées automatiquement</li>
                    <li>• Maintenance préventive mensuelle (2h maximum)</li>
                    <li>• Nouvelles fonctionnalités déployées progressivement</li>
                    <li>• Tests de charge et de performance réguliers</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="p-8 text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Besoin d'Éclaircissements ?
              </h2>
              <p className="mb-6 text-gray-600">
                Si vous avez des questions sur l'utilisation du service ou souhaitez signaler un problème, 
                notre équipe support est là pour vous aider.
              </p>
              <div className="space-x-4">
                <a
                  href="mailto:support@burok.app"
                  className="inline-flex items-center px-6 py-3 font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105"
                >
                  Contacter le Support
                </a>
                <Link
                  to="/cgu"
                  className="inline-flex items-center px-6 py-3 font-medium text-white bg-gray-600 rounded-full transition-all duration-200 hover:bg-gray-700"
                >
                  Retour aux CGU
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceUsage;
